import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, RefreshCwIcon, InfoIcon, ShieldCheckIcon, ExternalLinkIcon, CopyIcon, CheckIcon, GlobeIcon, UsersIcon, KeyIcon, MailIcon, EyeIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { emailNotificationService, EmailConfig } from '../../utils/emailNotifications';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
export function VerificationCodesConfig({
  isReadOnly = false
}: {
  isReadOnly?: boolean;
}) {
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const [config, setConfig] = useState<EmailConfig>({
    publicKey: '',
    serviceId: '',
    templateId: '',
    enabled: true,
    senderName: 'Savidna'
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: booleaan;
    message: string;
  } | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  // Template variables for verification codes
  const verificationVars = [{
    name: 'to_email',
    desc: 'Correo del usuario'
  }, {
    name: 'to_name',
    desc: 'Nombre del usuario'
  }, {
    name: 'from_name',
    desc: 'Nombre del remitente'
  }, {
    name: 'subject',
    desc: 'Asunto del correo'
  }, {
    name: 'message',
    desc: 'Código de verificación y mensaje'
  }];
  useEffect(() => {
    const loadConfig = async () => {
      const savedConfig = emailNotificationService.getConfig();
      if (savedConfig) {
        setConfig({
          ...savedConfig,
          enabled: true
        });
      }
      await emailNotificationService.loadFromSupabase();
      const freshConfig = emailNotificationService.getConfig();
      if (freshConfig) {
        setConfig({
          ...freshConfig,
          enabled: true
        });
      }
    };
    loadConfig();
  }, []);
  const handleSave = async () => {
    confirm({
      title: 'Guardar Configuración',
      message: '¿Estás seguro de que deseas guardar la configuración de códigos de verificación?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        setIsSaving(true);
        setSaveMessage('');
        const result = await emailNotificationService.saveConfig({
          ...config,
          enabled: true
        });
        if (result.success && !result.error) {
          showToast({
            type: 'success',
            title: 'Configuración guardada',
            message: 'Los códigos de verificación se enviarán por correo.'
          });
          setSaveMessage('✅ Configuración guardada. Los códigos de verificación se enviarán por correo.');
        } else if (result.success && result.error) {
          setSaveMessage(`⚠️ ${result.error}`);
        } else {
          setSaveMessage('❌ Error al guardar la configuración');
        }
        setIsSaving(false);
        setTimeout(() => setSaveMessage(''), 5000);
      }
    });
  };
  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    await emailNotificationService.saveConfig({
      ...config,
      enabled: true
    });
    if (!config.publicKey || !config.serviceId || !config.templateId) {
      setTestResult({
        success: false,
        message: 'Completa todos los campos: Public Key, Service ID y Template ID.'
      });
      setIsTesting(false);
      return;
    }
    setTestResult({
      success: true,
      message: '✅ Configuración válida. Los códigos de verificación se enviarán automáticamente por correo cuando un paciente se registre.'
    });
    setIsTesting(false);
  };
  const handleCopyVar = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 1500);
  };
  const isConfigComplete = config.publicKey && config.serviceId && config.templateId;
  return <div className="space-y-6" data-id="element-6234" data-mp="29krd5jcue56a">
      <div data-id="element-6235" data-mp="2ekx1oqntfbk5">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-6236" data-mp="2eknax0uy50de">
          <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-6237" data-mp="20kvxp2c97o0g" />
          Códigos de Verificación por Email
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-6238" data-mp="25ixl1zgpq9hq">
          Envía códigos de verificación de 6 dígitos a los pacientes cuando se
          registran
        </p>
      </div>

      {/* Status Card */}
      <Card className={`${isConfigComplete ? 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border-purple-200 dark:border-purple-800' : 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800'}`} data-id="element-6239" data-mp="3l1r4ocrd8fz9">
        <div className="flex items-center justify-between" data-id="element-6240" data-mp="19fgo3omx3mj9">
          <div className="flex items-center gap-4" data-id="element-6241" data-mp="1o3bhl78r1uxe">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${isConfigComplete ? 'bg-purple-500 dark:bg-purple-600 shadow-purple-500/20' : 'bg-amber-500 dark:bg-amber-600 shadow-amber-500/20'}`} data-id="element-6242" data-mp="1o43agaaypejz">
              <KeyIcon className="w-7 h-7 text-white" data-id="element-6243" data-mp="1t2obud1ya4a0" />
            </div>
            <div data-id="element-6244" data-mp="1t4r1ux33rxeq">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-6245" data-mp="271xiom49nm1b">
                {isConfigComplete ? '✅ Verificación Activa' : '⚠️ Configuración Pendiente'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-6246" data-mp="cp7mtyuc82ra">
                {isConfigComplete ? 'Los códigos se envían automáticamente al correo del paciente' : 'Configura EmailJS para enviar códigos de verificación reales'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 rounded-full border border-gray-200 dark:border-gray-700" data-id="element-6247" data-mp="2cqo0lffxwy6c">
            <UsersIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" data-id="element-6248" data-mp="2lrlvn88ehvcs" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400" data-id="element-6249" data-mp="2qq6yzgiy8v5g">
              Compartido
            </span>
          </div>
        </div>
      </Card>

      {/* How it works */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm" data-id="element-6250" data-mp="30fys06behg7t">
        <div className="flex items-start gap-3" data-id="element-6251" data-mp="34h29ex93jkb5">
          <InfoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-6252" data-mp="39gwycv9b9ztk" />
          <div data-id="element-6253" data-mp="39ihtalzjfkry">
            <p className="font-medium mb-1" data-id="element-6254" data-mp="39eu0jp1x32ea">¿Cómo funciona?</p>
            <ul className="text-blue-600 dark:text-blue-400 space-y-1 text-xs" data-id="element-6255" data-mp="3egja109hr809">
              <li data-id="element-6256" data-mp="3okpzi4y0kfxu">
                • Cuando un paciente se registra, se genera un código de 6
                dígitos
              </li>
              <li data-id="element-6257" data-mp="3jjagsjjb6lim">
                • El código se envía automáticamente a su correo electrónico
              </li>
              <li data-id="element-6258" data-mp="3enbur2lgdfld">• El paciente ingresa el código para verificar su email</li>
              <li data-id="element-6259" data-mp="3alq9otyrlv03">• Los códigos expiran en 10 minutos por seguridad</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Same config notice */}
      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg text-purple-700 dark:text-purple-300 text-sm flex items-start gap-2" data-id="element-6260" data-mp="65ffnfbwl2nz">
        <GlobeIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-6261" data-mp="3tjay5zxmark6" />
        <div data-id="element-6262" data-mp="3tgylo2amp9ei">
          <p className="font-medium" data-id="element-6263" data-mp="tv9qxsgkqj64">Usa la misma configuración de EmailJS</p>
          <p className="text-purple-600 dark:text-purple-400 mt-0.5" data-id="element-6264" data-mp="txe5d4c3n0kn">
            Los códigos de verificación usan la misma configuración de EmailJS
            que las notificaciones de citas. Si ya configuraste EmailJS, ¡ya
            está listo!
          </p>
        </div>
      </div>

      {saveMessage && <div className={`p-3 rounded-lg border text-sm ${saveMessage.startsWith('✅') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400' : saveMessage.startsWith('⚠️') ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'}`} data-id="element-6265" data-mp="yufkt0wmzj9q">
          {saveMessage}
        </div>}

      {/* EmailJS Configuration */}
      <Card data-id="element-6266" data-mp="1miythhjlly2c">
        <div className="space-y-4" data-id="element-6267" data-mp="1mif97u6h6xl5">
          <div className="flex items-center justify-between" data-id="element-6268" data-mp="1mlt9mwvkwwpy">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-6269" data-mp="646z4z2w6gqe">
              Credenciales de EmailJS
            </h3>
            <a href="https://dashboard.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1" data-id="element-6270" data-mp="b2gxtjntrzgr">
              Abrir Dashboard
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-6271" data-mp="2yay4ptnzrysa" />
            </a>
          </div>

          {isReadOnly && <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-700 dark:text-blue-300 flex items-center gap-3 text-sm shadow-sm" data-id="element-6272" data-mp="26luzoboa6kel">
              <EyeIcon className="w-5 h-5 flex-shrink-0" data-id="element-6273" data-mp="2fe6d8wyaqdd5" />
              <span className="font-medium" data-id="element-6274" data-mp="2ffpsqlc4okdv">
                Solo lectura — No tienes permisos para editar esta sección.
              </span>
            </div>}
          <Input label="Public Key" value={config.publicKey} onChange={e => {
          if (!isReadOnly) setConfig({
            ...config,
            publicKey: e.target.value
          });
        }} disabled={isReadOnly} placeholder="Ej: aBcDeFgHiJkLmN" helperText="Dashboard → Account → Public Key" data-id="element-6275" data-mp="1mx2e9orn03lr" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6276" data-mp="1f3engso0z5bs">
            <Input label="Service ID" value={config.serviceId} onChange={e => setConfig({
            ...config,
            serviceId: isReadOnly ? config.serviceId : e.target.value
          })} disabled={isReadOnly} placeholder="Ej: service_abc123" helperText="Dashboard → Email Services → Service ID" data-id="element-6277" data-mp="1a53cufpwoqsi" />
            <Input label="Template ID" value={config.templateId} onChange={e => setConfig({
            ...config,
            templateId: isReadOnly ? config.templateId : e.target.value
          })} disabled={isReadOnly} placeholder="Ej: template_xyz789" helperText="Dashboard → Email Templates → Template ID" data-id="element-6278" data-mp="mgkdwqsqxqd3" />
          </div>

          <Input label="Nombre del Remitente" value={config.senderName} onChange={e => setConfig({
          ...config,
          senderName: isReadOnly ? config.senderName : e.target.value
        })} disabled={isReadOnly} placeholder="Savidna - Centro de Bienestar" data-id="element-6279" data-mp="330w5dckgvcth" />

          <div className="flex items-center gap-3 pt-4 flex-wrap" data-id="element-6280" data-mp="2jdq3b88f958u">
            {!isReadOnly && <Button onClick={handleSave} isLoading={isSaving} data-id="element-6281" data-mp="2o710ehnfm7nk">
                Guardar
              </Button>}
            <Button variant="secondary" onClick={handleTestConnection} disabled={isReadOnly} isLoading={isTesting} disabled={!isConfigComplete} data-id="element-6282" data-mp="1rm0p5m6693at">
              <RefreshCwIcon className="w-4 h-4" data-id="element-6283" data-mp="2u1rh0eshx5wm" />
              Verificar
            </Button>
            <Button variant="secondary" onClick={() => setShowInstructions(!showInstructions)} data-id="element-6284" data-mp="38qnnw8itgcvn">
              <InfoIcon className="w-4 h-4" data-id="element-6285" data-mp="33rkwhqvre565" />
              {showInstructions ? 'Ocultar' : 'Ver'} Instrucciones
            </Button>
          </div>

          {testResult && <div className={`p-3 rounded-lg border flex items-start gap-2 ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'}`} data-id="element-6286" data-mp="2b3fal0syya4q">
              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-6287" data-mp="avmfuwywdbq" /> : <XCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-6288" data-mp="7u4v1lxng7zq" />}
              <p className="font-medium whitespace-pre-line" data-id="element-6289" data-mp="csycfpg24j56">
                {testResult.message}
              </p>
            </div>}
        </div>
      </Card>

      {/* Instructions */}
      {showInstructions && <Card data-id="element-6290" data-mp="2tz1u1bqedfj">
          <div className="space-y-6" data-id="element-6291" data-mp="2vju1kakpka8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-6292" data-mp="2wlicqglo39w">
              Configuración Rápida
            </h3>

            <div className="space-y-6" data-id="element-6293" data-mp="vjz6q068fqie">
              {/* Step 1 */}
              <div className="flex gap-4" data-id="element-6294" data-mp="vlaeeb2sties">
                <div className="flex-shrink-0" data-id="element-6295" data-mp="gti8sqst3iq9">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-6296" data-mp="gsp8a7kfxpgk">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-6297" data-mp="19ip4zj4sfw8c">
                      1
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-6298" data-mp="3mqqdhou2xqrs">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-6299" data-mp="3mrrqhyunonc5">
                    Crea una cuenta en EmailJS
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-6300" data-mp="3csu16tpeops3">
                    Ve a{' '}
                    <a href="https://www.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline" data-id="element-6301" data-mp="37tzcjb4rc702">
                      emailjs.com
                    </a>{' '}
                    y regístrate gratis (200 correos/mes).
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4" data-id="element-6302" data-mp="s2l7j8ucmvpl">
                <div className="flex-shrink-0" data-id="element-6303" data-mp="s3mtw9gtf4mm">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-6304" data-mp="s4fvyx4tx5yf">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-6305" data-mp="vl0sm7li3dl8">
                      2
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-6306" data-mp="14lgvbeuf6gp1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-6307" data-mp="14kfjv99hrs6s">
                    Conecta tu Gmail
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-6308" data-mp="1ejd3pyvz2xjg">
                    Dashboard → Email Services → Add New Service → Gmail. Copia
                    el <strong data-id="element-6309" data-mp="1ijf7cdl9q11d">Service ID</strong>.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4" data-id="element-6310" data-mp="1sarzl6azat7x">
                <div className="flex-shrink-0" data-id="element-6311" data-mp="1s9qcg3goug9u">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-6312" data-mp="1scm8fm8z6uvn">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-6313" data-mp="228fubw8l40q3">
                      3
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-6314" data-mp="17lz4yo05i9u3">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-6315" data-mp="17n1tipa0406u">
                    Crea o usa tu plantilla existente
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-6316" data-mp="1hk6j6z322dse">
                    Si ya tienes una plantilla para notificaciones de citas,{' '}
                    <strong data-id="element-6317" data-mp="1vdebyu2vhfbo">¡úsala!</strong> Los códigos de verificación usan la
                    misma plantilla. Solo asegúrate de incluir la variable{' '}
                    <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded" data-id="element-6318" data-mp="258g0bnpbqdd7">
                      {'{{message}}'}
                    </code>
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-6319" data-mp="av6npn1hebqq">
                    <div className="grid grid-cols-1 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6320" data-mp="5xewkv8nutnl">
                      {verificationVars.map(v => <div key={v.name} className="flex items-center justify-between px-3 py-2" data-id="element-6321" data-mp="2mitnidbrrqvi">
                          <div className="flex items-center gap-2" data-id="element-6322" data-mp="27rt79m4s6sot">
                            <code className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded font-mono" data-id="element-6323" data-mp="2crpahj5nmg3b">
                              {`{{${v.name}}}`}
                            </code>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6324" data-mp="22shok7kutd4l">
                              {v.desc}
                            </span>
                          </div>
                          <button onClick={() => handleCopyVar(v.name)} className="p-1 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 rounded transition-colors" title="Copiar" data-id="element-6325" data-mp="1tvz7ptpwdfod">
                            {copiedVar === v.name ? <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-6326" data-mp="2710hofyvgdte" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-6327" data-mp="22760pkanicy0" />}
                          </button>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4" data-id="element-6328" data-mp="1naey725okl46">
                <div className="flex-shrink-0" data-id="element-6329" data-mp="1j9uzvv9dfbgg">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center" data-id="element-6330" data-mp="1jaoaj7bgal3v">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-lg" data-id="element-6331" data-mp="1ededp6lqf760">
                      4
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-6332" data-mp="38mzws844qmdr">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-6333" data-mp="3g93s5xbasdyc">
                    Pega las credenciales aquí
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-6334" data-mp="3l5ls8jvono4c">
                    Copia el Public Key, Service ID y Template ID de EmailJS y
                    pégalos arriba. Haz clic en Guardar y ¡listo!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4" data-id="element-6335" data-mp="2xbu7kcu8kufi">
              <p className="text-sm text-purple-700 dark:text-purple-300" data-id="element-6336" data-mp="3785lt8ddzucl">
                <strong data-id="element-6337" data-mp="37a04sh53lce1">✅ ¡Listo!</strong> Los códigos de verificación se
                enviarán automáticamente al correo del paciente cuando se
                registre. El código expira en 10 minutos.
              </p>
            </div>
          </div>
        </Card>}

      {/* Example email preview */}
      <Card data-id="element-6338" data-mp="rgyynfi387aq">
        <div className="space-y-3" data-id="element-6339" data-mp="rh8moxjko4ds">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-6340" data-mp="rg7er1xury0a">
            <MailIcon className="w-5 h-5 text-purple-600" data-id="element-6341" data-mp="vh11hrzk76rl" />
            Vista Previa del Correo
          </h3>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4" data-id="element-6342" data-mp="10a2ji8v2bmfe">
            <div className="space-y-2 text-sm" data-id="element-6343" data-mp="15ahyf4uzt1zh">
              <p className="font-semibold text-gray-900 dark:text-white" data-id="element-6344" data-mp="8iwbz7d0img3">
                Asunto: 🔐 Código de Verificación - {config.senderName}
              </p>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 text-gray-600 dark:text-gray-400" data-id="element-6345" data-mp="hiswqxwa715v">
                <p data-id="element-6346" data-mp="mif9z7c40heo">Hola,</p>
                <p className="mt-2" data-id="element-6347" data-mp="mip1x0gpsyn0">Tu código de verificación es:</p>
                <p className="my-3 text-2xl font-bold text-purple-600 dark:text-purple-400" data-id="element-6348" data-mp="1syhkhrhd047q">
                  123456
                </p>
                <p className="text-xs text-gray-500" data-id="element-6349" data-mp="1xvrm05i0w6bx">
                  Este código expira en 10 minutos.
                </p>
                <p className="mt-3 text-xs text-gray-500" data-id="element-6350" data-mp="3ih6aiyspyy5v">
                  Si no solicitaste este código, ignora este correo.
                </p>
                <p className="mt-4 text-xs" data-id="element-6351" data-mp="38oig2ap7p6ab">
                  ---
                  <br data-id="element-6352" data-mp="33q75tyv08trk" />
                  Saludos,
                  <br data-id="element-6353" data-mp="33pfnvqubyujr" />
                  {config.senderName}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
}